<template>
    <div
        class="size-full fixed top-0 left-0 z-50 flex overflow-hidden"
        :class="{
            'sm:p-2.5 md:p-10 3xl:p-9': !type,
            'px-2.5 md:px-10 3xl:px-9 pt-[60px] sm:pt-small pb-small': type === 'full',
        }"
    >
        <div
            class="size-full fixed top-0 left-0"
            :class="{
                'bg-brand-2-950/30': !type,
                'bg-brand-2-950': type === 'full',
            }"
            @click.prevent="$emit('close')"
        ></div>

        <ClearButton
            v-if="type == 'full'"
            class="absolute top-3 right-3 md:top-5 md:right-5 border-white/20 hover:border-white/40 text-white"
            @click.prevent="$emit('close')"
        />

        <div
            class="w-full m-auto h-full sm:h-auto sm:max-h-full overflow-auto relative"
            :class="{
                'max-w-small bg-white sm:rounded-xl shadow-4xl flex flex-col': !type,
            }"
        >
            <div :class="{
                'p-4 md:p-10': !type,
            }">
                <div
                    v-if="!type"
                    class="flex items-center justify-between mb-3 sm:mb-5"
                >
                    <h6
                        class="h6 heading"
                        v-text="title"
                    ></h6>

                    <ClearButton @click.prevent="$emit('close')" />
                </div>

                <slot />
            </div>

            <div
                v-if="$slots.footer"
                class="px-4 md:px-10 sticky bottom-0 bg-white mt-auto"
            >
                <div class="pt-4 md:pt-5 pb-4 md:pb-10 border-t border-brand-2-950/10">
                    <slot name="footer" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { watch, onBeforeUnmount } from 'vue';

    import ClearButton from '@js/src/components/ClearButton.vue';

    const emits = defineEmits(['close']);

    const props = defineProps({
        title: String,
        type: {
            type: String,
            default: '',
        },
        // Mirrors the caller's v-show. While a modal is open the BODY is frozen
        // in place (position: fixed at the current scroll offset) - not for
        // scroll-bleed cosmetics, but because iOS Safari mis-scrolls the
        // document when an input inside a fixed overlay is focused over a
        // scrolled page: the page lurches and the caret is painted outside the
        // field. Freezing the body removes the document scroll iOS gets wrong.
        active: {
            type: Boolean,
            default: false,
        },
    });

    let savedScrollY = 0;

    function lockBody() {
        savedScrollY = window.scrollY;

        const body = document.body;
        body.style.position = 'fixed';
        body.style.top = `-${savedScrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
    }

    function unlockBody() {
        const body = document.body;
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';

        // 'instant' so a global smooth-scroll setting can never animate the
        // restore - the user must not see the page fly back.
        window.scrollTo({ top: savedScrollY, behavior: 'instant' });
    }

    watch(() => props.active, (active, previous) => {
        if (active) {
            lockBody();
        } else if (previous) {
            unlockBody();
        }
    });

    onBeforeUnmount(() => {
        if (props.active) {
            unlockBody();
        }
    });
</script>
